import { adminApi } from './admin-api';

export type TicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_for_user'
  | 'resolved'
  | 'closed';

/** « repris » : une personne a répondu après une remontée — plus rien à signaler. */
export type AgentEtat = 'repondu' | 'remonte' | 'repris';

export interface SupportTicket {
  uuid: string;
  subject: string;
  status: TicketStatus;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  origin: 'portal' | 'email';
  requesterEmail: string | null;
  requesterName: string | null;
  /** Ce que l'assistant du support a fait de la demande (null : rien encore). */
  agentEtat: AgentEtat | null;
  agentMotif: string | null;
  agentTraiteLe: string | null;
  replyCount: number;
  createdAt: string;
  updatedAt: string;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  user: {
    uuid: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  assignedTo: {
    uuid: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface SupportTicketDetail extends SupportTicket {
  content: string;
  attachments: any[] | null;
  /** Réponse préparée par l'assistant quand il a remonté la demande, jamais envoyée. */
  agentBrouillon: string | null;
  replies: SupportTicketReply[];
}

export interface SupportTicketReply {
  uuid: string;
  content: string;
  authorRole: 'user' | 'admin' | 'external' | 'assistant';
  authorName: string;
  authorEmail?: string;
  attachments: any[] | null;
  createdAt: string;
}

export interface SupportStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  avgResponseTimeMinutes: number | null;
}

export interface SupportTicketFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  priority?: string;
  agent?: AgentEtat;
  sortBy?: string;
  sortOrder?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const adminSupportService = {
  getStats: async (): Promise<SupportStats> => {
    const response = await adminApi.get('/admin/support/stats');
    return response.data;
  },

  getTickets: async (
    filters: SupportTicketFilters = {},
  ): Promise<PaginatedResponse<SupportTicket>> => {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.agent) params.set('agent', filters.agent);
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
    const response = await adminApi.get(
      `/admin/support/tickets?${params.toString()}`,
    );
    return response.data;
  },

  getTicketDetail: async (uuid: string): Promise<SupportTicketDetail> => {
    const response = await adminApi.get(`/admin/support/tickets/${uuid}`);
    return response.data;
  },

  reply: async (
    uuid: string,
    content: string,
  ): Promise<SupportTicketReply> => {
    const response = await adminApi.post(
      `/admin/support/tickets/${uuid}/reply`,
      { content },
    );
    return response.data;
  },

  updateTicket: async (
    uuid: string,
    data: { status?: string; priority?: string },
  ): Promise<SupportTicket> => {
    const response = await adminApi.patch(
      `/admin/support/tickets/${uuid}`,
      data,
    );
    return response.data;
  },
};
